import api from '../api/apiClient';
import { toTopology } from './graphTopologyAdapter';
import { graphLensService } from '../graph/graphLensService';
import agentAtlasService from '../agent-atlas/agentAtlasService';
import { managementService } from '../management/managementService';
import observabilityService from '../observabilityService';
import { reconcileCustomTopology } from './customTopologyReconciler';
import { daysForTimeRange, normalizeTimeRange } from '../../utils/timeRange';

// The Agent Network's group members (network/estate.py). The topology itself comes from the
// graph-lens service below; do not route the network read through this prefix.
const ATLAS = '/agent-atlas/api/v1';
// The Custom surface's trace topology, reconciled into getTopology.
const OPS = '/ops/api/v1';

/** Map the graph's rolling day count to the nearest supported Ops preset. */
function timeRangeToOpsPreset(tr, days) {
  const preset = normalizeTimeRange(tr);
  if (preset) return preset;
  const count = Number(days);
  if (!Number.isFinite(count) || count <= 0) return 'last_90_days';
  if (count <= 1) return 'last_24_hours';
  if (count <= 7) return 'last_7_days';
  if (count <= 30) return 'last_30_days';
  if (count <= 90) return 'last_90_days';
  if (count <= 180) return 'last_6_months';
  return 'last_year';
}

/**
 * Agent Network Service
 *
 * Neo4j supplies the relationship graph. For the Custom surface, the dated Agent Atlas
 * inventory and observability aggregates reconcile the active agents and models because
 * those stores receive telemetry before the asynchronous graph materializer does. The
 * management registry supplies display names and bridges stable identifiers to numeric
 * ids; it does not make inactive agents appear in a dated topology.
 */
export const agentNetworkService = {
  /**
   * Get agent network topology from the entity graph.
   * @param {Object} options
   * @param {string} [options.timeRange] - e.g. 'last_30_days'; scopes the graph to edges
   *        seen in that window so the 1D/7D/30D/90D filter narrows the topology.
   * @param {string} [options.surface] - custom reconciles agents/models with the
   *        selected-window Dashboard sources.
   * @returns {Promise<Object>} Topology data with nodes, links, and stats
   */
  async getTopology(options = {}) {
    try {
      // With `surface`, the server scopes the graph to it. Without one, the whole estate
      // comes back capped at 600 nodes, and a module filtering that in the browser loses
      // whatever the cap cut off (on org 3, an Anthropic model and app the AI-BOM lists).
      // Custom agents are scoped to their graph surface explicitly. The graph worker
      // stamps custom-agent nodes `surfaces: ['custom']` (custom_agents.yaml
      // surface_default). Leaving `surface` unset did NOT fetch unscoped inside the
      // Custom module: withSurface then filled in the module's pinned SECURITY surface,
      // `otel`, which no graph node carries, so the graph came back with no agents,
      // datastores or MCP servers and Source Intelligence was always empty.
      const started = performance.now();
      // Translate the page's time-range string into a day window the graph endpoint
      // understands; edges carry last_seen, so this actually filters the topology.
      const days = daysForTimeRange(options.timeRange);
      const params = { limit: 600, scope: 'all' };
      if (days) params.days = days;
      if (options.surface) params.surface = options.surface;
      // Use the canonical graph-lens contract. The retired Agent Atlas graph endpoint
      // has its own stale Cypher and can return only a model node for short windows even
      // while the lens and the activity APIs contain active Custom agents.
      const graphPromise = graphLensService.graphNetwork(params);
      const supplementalPromise = options.surface === 'custom'
        ? Promise.allSettled([
          agentAtlasService.listAgents({ surface: 'custom', limit: 1000, ...(days ? { days } : {}) }),
          managementService.getAgents(),
          observabilityService.getLlmMetrics(timeRangeToOpsPreset(options.timeRange, days), null),
          api.get(`${OPS}/agent-network/topology`, {
            params: { time_range: timeRangeToOpsPreset(options.timeRange, days) },
          }),
        ])
        : Promise.resolve(null);
      const [graph, supplemental] = await Promise.all([graphPromise, supplementalPromise]);
      let topo = toTopology(graph);
      if (supplemental) {
        const [inventoryResult, registryResult, metricsResult, traceTopologyResult] = supplemental;
        const inventory = inventoryResult.status === 'fulfilled' ? inventoryResult.value : null;
        const llmMetrics = metricsResult.status === 'fulfilled' ? metricsResult.value?.data : null;
        topo = reconcileCustomTopology(topo, {
          inventory,
          registry: registryResult.status === 'fulfilled' ? registryResult.value : null,
          llmMetrics,
          traceTopology: traceTopologyResult.status === 'fulfilled'
            ? traceTopologyResult.value?.data : null,
          inventoryKnown: Array.isArray(inventory) || Array.isArray(inventory?.items),
          metricsKnown: Boolean(llmMetrics) && (
            Array.isArray(llmMetrics.model_performance)
            || Array.isArray(llmMetrics.model_distribution?.labels)
          ),
        });
      }
      topo.source = 'graph';
      topo.fetch_ms = Math.round(performance.now() - started);
      return topo;
    } catch (error) {
      console.error('[Agent Network Service] Error fetching topology:', error);
      throw new Error(
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.message ||
        'Failed to fetch agent network topology'
      );
    }
  },

  /**
   * The members of one Agent Network group, a page at a time, best connected first.
   *
   * The group and its size come from the metrics engine (network.members); this lists the
   * Context Graph nodes behind it from the same definition (Atlas GET /network/members), with
   * the same window, surface pin and grouping, so the list and the number agree.
   * @param {Object} options
   * @param {string} options.group - a network.members group key (e.g. "agent:posture:incident")
   * @param {string} options.grouping - pattern | posture | surface
   * @param {Object} options.window - {days} or {start, end} (useMetrics' windowFromDateParams)
   * @param {string} [options.surface] - the module's surface pin; none on Atlas
   * @returns {Promise<{items: Array, next_offset: number|null}>}
   */
  async getGroupMembers({ group, grouping, window = {}, surface, offset = 0, limit = 25, agents, users } = {}) {
    const params = { group, grouping, offset, limit };
    // A module's agent / user selection (comma lists), as the network metrics' agent / user filters.
    if (agents?.length) params.agent = agents.join(',');
    if (users?.length) params.user = users.join(',');
    if (window.start && window.end) {
      params.start = window.start;
      params.end = window.end;
    } else if (window.days) {
      params.days = window.days;
    }
    try {
      params.tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    } catch (e) { /* the server's default zone */ }
    if (surface) params.surface = surface;
    const res = await api.get(`${ATLAS}/network/members`, { params });
    return res.data;
  },
};

export default agentNetworkService;
